import React from 'react';
import {
  Container,
  Divider,
  Grid,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ViewUnitContentProps } from '../../types/viewUnitContentProps';
import CopyIcon from '@mui/icons-material/CopyAllRounded';
import useCopyToClipboard from '../../../../ui/helpers/copy-to-clipboard';
import { Notifier } from '../../../../system/services/notificationManager';
import { useAuthenticator } from '../../../../auth/AuthenticationContext';
import { AuthorizedUser } from '../../../../auth/models';
import { useEffect } from 'react';

export const ViewResidentContent = ({
  unit,
  property,
}: ViewUnitContentProps) => {
  const { title, address, propMgr, owner } = property || {};

  const { line1, city, state, postalCode } = address || {};
  const addressLine = `${line1 || 'Unknown Address'}`;
  const cityStateZip = `${city || 'Unknown City'}, ${state || '??'}  ${
    postalCode || '00000'
  }`;
  const fullAddress = `${addressLine}, ${cityStateZip}`;
  const [clipboardValue, copyToClipboard] = useCopyToClipboard();
  const auth = useAuthenticator();
  const [user, setUser] = React.useState<AuthorizedUser>();

  useEffect(() => {
    setUser(auth?.user ? auth.user : undefined);
  }, [auth]);

  return unit && property ? (
    <Container style={{ padding: '0 10px' }}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h3>Unit Information</h3>
        </div>
        {user?.isSuper && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <IconButton
              aria-label="copy to clipboard"
              onClick={async () => {
                if (unit._id) {
                  await copyToClipboard(unit._id);
                  console.log('Copied to clipboard', clipboardValue);
                  Notifier.success('Unit ID copied to clipboard');
                } else {
                  Notifier.error('No Unit ID available to copy to clipboard');
                }
              }}
            >
              <Tooltip title="Click to copy Unit ID">
                <CopyIcon />
              </Tooltip>
            </IconButton>
          </div>
        )}
      </div>
      <Stack direction="column" sx={{ px: '24px' }}>
        <div>
          <span style={{ fontWeight: 'bold' }}>Unit: </span>
          <span>{unit.name ?? 'N/A'}</span>
        </div>
        <div>
          <span style={{ fontWeight: 'bold' }}>Type: </span>
          <span style={{ textTransform: 'capitalize' }}>
            {unit.type ?? 'N/A'}
          </span>
        </div>
        <div>
          <span style={{ fontWeight: 'bold' }}>Building: </span>
          <span>{unit.building?.name ?? 'Unnamed Building'}</span>
        </div>
      </Stack>
      <Divider sx={{ marginTop: '20px', borderColor: 'rgba(0, 0, 0, 0.5)' }} />
      {unit.resident?.name || unit.resident?.email ? (
        <>
          <h3>Resident Contact</h3>
          <Stack direction="column" sx={{ px: '24px' }}>
            <div>
              <span style={{ fontWeight: 'bold' }}>Full Name: </span>
              <span>{unit.resident?.name ?? 'N/A'}</span>
            </div>
            <div>
              <span style={{ fontWeight: 'bold' }}>Email: </span>
              <span>{unit.resident?.email ?? 'N/A'}</span>
            </div>
          </Stack>
          <Stack sx={{ px: '24px' }}>
            <Grid container spacing={3}>
              <Grid item>
                <div>
                  <span style={{ fontWeight: 'bold' }}>Phone: </span>
                  <span>{unit.resident?.phone ?? 'N/A'}</span>
                </div>
                <div>
                  <span style={{ fontWeight: 'bold' }}>Mobile: </span>
                  <span>{unit.resident?.mobile ?? 'N/A'}</span>
                </div>
              </Grid>
            </Grid>
          </Stack>
        </>
      ) : (
        <Stack direction="column">
          <h3>Resident Contact</h3>
          <div style={{ paddingLeft: '24px' }}>No registered resident</div>
        </Stack>
      )}
      <Divider sx={{ marginTop: '20px', borderColor: 'rgba(0, 0, 0, 0.5)' }} />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <h3>Property Information</h3>
        </div>
        {user?.isSuper && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <IconButton
              aria-label="copy to clipboard"
              onClick={async () => {
                if (unit._id) {
                  await copyToClipboard(property?._id);
                  console.log('Copied to clipboard', clipboardValue);
                  Notifier.success('Property ID copied to clipboard');
                } else {
                  Notifier.error(
                    'No Property ID available to copy to clipboard',
                  );
                }
              }}
            >
              <Tooltip title="Click to copy Property ID">
                <CopyIcon />
              </Tooltip>
            </IconButton>
          </div>
        )}
      </div>
      <Stack direction="column" sx={{ px: '24px' }}>
        <div>
          <span style={{ fontWeight: 'bold' }}>Title: </span>
          <span>{property?.title ?? 'N/A'}</span>
        </div>
        <div>
          <span style={{ fontWeight: 'bold' }}>Address: </span>
          <span>{fullAddress}</span>
        </div>
        <div>
          <span style={{ fontWeight: 'bold' }}>Manager: </span>
          <span>{propMgr?.name ?? 'Unnamed Manager'}</span>
        </div>
        <div>
          <span style={{ fontWeight: 'bold' }}>Owner: </span>
          <span>{owner?.name ?? 'Unnamed Owner'}</span>
        </div>
      </Stack>
    </Container>
  ) : null;
};
